/**
 * iKRDiagram Validation Schema
 */

import { z } from 'zod';
import { evaluateExpression } from '../shared';

export const baseSchema = z.object({
  project: z.string().min(1, 'Project name is required'),
  title: z.string().min(1, 'Title is required'),

  width: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Width must be a positive number'),

  length_o: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Length (origin) must be a positive number'),

  length: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Length must be a positive number'),

  weight: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Weight must be a positive number'),

  klmFactor: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0 && n <= 1;
  }, 'KLM factor must be a number between 0 and 1'),

  ductility: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Ductility must be a greater than 0'),

  rotation: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Rotation must be a greater than 0'),

  loadType: z.string().refine(val => ['0', '1', '2'].includes(val), { message: 'Load type must be 0, 1, or 2' }),

  pressure: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Pressure must be greater than 0'),

  impulse: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Impulse must be greater than 0'),

  resistance: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Resistance must be greater than 0'),

  deflection: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Deflection must be a positive number')
});

/**
 * Full schema with cross-field validation
 */
export const schema = baseSchema.superRefine((data, ctx) => {
  if (evaluateExpression(data.length_o) != null && evaluateExpression(data.length) != null) {
    if (evaluateExpression(data.length_o)! > evaluateExpression(data.length)!) {
      ctx.addIssue({
        code: 'custom',
        message: 'Length (origin) cannot be greater than Length.',
        path: ['length_o']
      });
    }
  }
});

/**
 * Helper types
 */
export type FieldName = keyof z.infer<typeof baseSchema>;
export type FormValues = z.input<typeof schema>; // raw form shape (strings from inputs/selects)
export type ParsedValues = z.output<typeof schema>; // parsed shape (numbers after coercion)
export type FormErrors = Partial<Record<FieldName, string | null>>;

/**
 * Validate a single field
 */
export function validateOneField<K extends FieldName>(key: K, raw: unknown): string | null {
  const fieldSchema = baseSchema.shape[key];
  const res = fieldSchema.safeParse(raw);
  return res.success ? null : (res.error.issues[0]?.message ?? 'Invalid value');
}
